import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';
import Icon from './Icon';
import './OnOffboardForm.css';

const getLicenseDisplayName = (license) => (license?.productName || '').trim();

const templateFieldKey = (field, index = 0) => {
  if (field?.id != null && field.id !== '') return String(field.id);
  return [
    'f',
    index,
    String(field?.maincaption || '').trim().toLowerCase(),
    String(field?.widgetgroupname || '').trim().toLowerCase(),
    String(field?.displayname || '').trim().toLowerCase(),
    String(field?.fieldtype || '').trim().toLowerCase()
  ].join('::');
};

const isManagerField = (field) =>
  String(field?.displayname || '').trim().toLowerCase() === 'manager';

const isTimeLimitedField = (field) =>
  String(field?.displayname || '').toLowerCase().includes('time-limited');

const defaultValueForField = (field) => {
  const type = String(field?.fieldtype || '').toLowerCase();
  const raw = field?.defaultvalue ?? field?.defaultValue;

  if (type === 'checkbox') {
    const normalized = String(raw ?? '').trim().toLowerCase();
    return ['1', 'true', 'yes', 'checked', 'on'].includes(normalized);
  }
  if (type === 'yesno') {
    return String(raw ?? '').trim().toLowerCase() === 'yes' ? 'yes' : 'no';
  }
  if (raw == null || raw === '') {
    return '';
  }
  return String(raw);
};

const findTemplateValueByNames = (fields, values, names) => {
  const nameSet = new Set(names.map((n) => n.toLowerCase()));
  for (let i = 0; i < fields.length; i += 1) {
    const field = fields[i];
    const label = String(field?.displayname || '').trim().toLowerCase();
    if (!nameSet.has(label)) continue;
    const key = templateFieldKey(field, i);
    return values[key];
  }
  return '';
};

const OnOffboardForm = ({ onBack }) => {
  const { selectedTenant, authenticatedFetch } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    phoneNumber: '',
    manager: '',
    action: 'onboard', // 'onboard' or 'offboard'
    employerLeavingDate: '',
    employeeStartDate: '',
    timeLimitedContract: 'no',
    contractEndDate: '',
    existingUsername: '',
    additionalInformation: '',
    substituteFor: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [ticketUrl, setTicketUrl] = useState('');
  const [usernameSuggestions, setUsernameSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [substituteSuggestions, setSubstituteSuggestions] = useState([]);
  const [showSubstituteSuggestions, setShowSubstituteSuggestions] = useState(false);
  const [isLoadingSubstituteSuggestions, setIsLoadingSubstituteSuggestions] = useState(false);
  const [managerSuggestions, setManagerSuggestions] = useState([]);
  const [showManagerSuggestions, setShowManagerSuggestions] = useState(false);
  const [isLoadingManagerSuggestions, setIsLoadingManagerSuggestions] = useState(false);
  const [userAssets, setUserAssets] = useState([]);
  const [userLicenses, setUserLicenses] = useState([]);
  const [assetActions, setAssetActions] = useState({});
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [templateSections, setTemplateSections] = useState([]);
  const [templateFields, setTemplateFields] = useState([]);
  const [templateValues, setTemplateValues] = useState({});
  const [templateLoading, setTemplateLoading] = useState(false);

  const ASSET_ACTION_OPTIONS = [
    { value: 'wipe', label: 'Wipe' },
    { value: 'nothing', label: 'Do nothing' },
    { value: 'retire', label: 'Retire' }
  ];

  const getAssetKey = (asset) => {
    if (asset?.id != null) return String(asset.id);
    if (asset?.serial) return String(asset.serial);
    return '';
  };

  const applyTemplateFields = useCallback((fields) => {
    const list = Array.isArray(fields) ? fields : [];
    const values = {};
    list.forEach((field, index) => {
      values[templateFieldKey(field, index)] = defaultValueForField(field);
    });
    setTemplateFields(list);
    setTemplateValues(values);
  }, []);

  const fetchOnboardingTemplate = useCallback(async () => {
    const tenantName = selectedTenant?.name;
    if (!tenantName || formData.action !== 'onboard') {
      setTemplateSections([]);
      setTemplateFields([]);
      setTemplateValues({});
      return;
    }
    try {
      setTemplateLoading(true);
      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/onboarding-form-options?tenant=${encodeURIComponent(tenantName)}`
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        setTemplateSections([]);
        setTemplateFields([]);
        setTemplateValues({});
        return;
      }
      const fields = Array.isArray(data.fields) ? data.fields : [];
      setTemplateSections(Array.isArray(data.sections) ? data.sections : []);
      applyTemplateFields(fields);
    } catch (err) {
      console.error('Error loading onboarding template:', err);
      setTemplateSections([]);
      setTemplateFields([]);
      setTemplateValues({});
    } finally {
      setTemplateLoading(false);
    }
  }, [authenticatedFetch, selectedTenant, formData.action, applyTemplateFields]);

  useEffect(() => {
    fetchOnboardingTemplate();
  }, [fetchOnboardingTemplate]);

  const timeLimitedValue = useMemo(() => {
    for (let i = 0; i < templateFields.length; i += 1) {
      const field = templateFields[i];
      if (!isTimeLimitedField(field) || field.fieldtype !== 'yesno') continue;
      return templateValues[templateFieldKey(field, i)] || 'no';
    }
    return formData.timeLimitedContract || 'no';
  }, [templateFields, templateValues, formData.timeLimitedContract]);

  const setTemplateFieldValue = (field, index, value) => {
    const key = templateFieldKey(field, index);
    setTemplateValues((prev) => ({ ...prev, [key]: value }));

    const label = String(field?.displayname || '').trim().toLowerCase();
    if (label === 'name') setFormData((prev) => ({ ...prev, name: value }));
    if (label === 'surname') setFormData((prev) => ({ ...prev, surname: value }));
    if (label === 'phone number') setFormData((prev) => ({ ...prev, phoneNumber: value }));
    if (label === 'manager') setFormData((prev) => ({ ...prev, manager: value }));
    if (label === 'employee start date') setFormData((prev) => ({ ...prev, employeeStartDate: value }));
    if (isTimeLimitedField(field)) {
      setFormData((prev) => ({
        ...prev,
        timeLimitedContract: value,
        contractEndDate: value === 'yes' ? prev.contractEndDate : ''
      }));
    }
    if (label.includes('additional information')) {
      setFormData((prev) => ({ ...prev, additionalInformation: value }));
    }
  };

  const buildTemplateFieldsPayload = () =>
    templateFields.map((field, index) => {
      const key = templateFieldKey(field, index);
      return {
        id: field.id,
        maincaption: field.maincaption,
        sectiondescription: field.sectiondescription,
        widgetgroupname: field.widgetgroupname,
        displayname: field.displayname,
        fieldtype: field.fieldtype,
        description: field.description,
        mandatory: Boolean(field.mandatory),
        defaultvalue: field.defaultvalue,
        value: templateValues[key]
      };
    });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const nextValue = type === 'checkbox' ? checked : value;
      const next = {
        ...prev,
        [name]: nextValue
      };

      // If contract is no longer time-limited, clear any previously selected end date.
      if (name === 'timeLimitedContract' && nextValue !== 'yes') {
        next.contractEndDate = '';
      }

      return next;
    });

    // Handle username typeahead
    if (name === 'existingUsername' && value.length >= 2) {
      fetchUsernameSuggestions(value);
    } else if (name === 'existingUsername' && value.length < 2) {
      setUsernameSuggestions([]);
      setShowSuggestions(false);
    }

    // Handle substitute typeahead (offboarding only)
    if (name === 'substituteFor' && value.length >= 2) {
      fetchSubstituteSuggestions(value);
    } else if (name === 'substituteFor' && value.length < 2) {
      setSubstituteSuggestions([]);
      setShowSubstituteSuggestions(false);
    }

    // Handle manager typeahead
    if (name === 'manager' && value.length >= 2) {
      fetchManagerSuggestions(value);
    } else if (name === 'manager' && value.length < 2) {
      setManagerSuggestions([]);
      setShowManagerSuggestions(false);
    }
  };

  const fetchUsernameSuggestions = async (query) => {
    if (!selectedTenant || query.length < 2) {
      setUsernameSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      setIsLoadingSuggestions(true);
      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/users/offboarding-assignees?tenant=${encodeURIComponent(
          selectedTenant.name
        )}&q=${encodeURIComponent(query)}`
      );
      const data = await response.json();

      if (response.ok && data.success && Array.isArray(data.suggestions)) {
        const lower = query.toLowerCase();
        const suggestions = data.suggestions
          .filter((label) => label && String(label).toLowerCase().includes(lower))
          .slice(0, 10);

        setUsernameSuggestions(suggestions);
        setShowSuggestions(suggestions.length > 0);
      } else {
        setUsernameSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching username suggestions:', error);
      setUsernameSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const fetchSubstituteSuggestions = async (query) => {
    if (!selectedTenant || query.length < 2) {
      setSubstituteSuggestions([]);
      setShowSubstituteSuggestions(false);
      return;
    }

    try {
      setIsLoadingSubstituteSuggestions(true);
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/assets?tenant=${selectedTenant.name}&search=${encodeURIComponent(query)}&field=username`);
      const data = await response.json();

      if (response.ok && data.assets) {
        const existingUsernameLower = (formData.existingUsername || '').toLowerCase();
        const suggestions = data.assets
          .map(asset => asset.username)
          .filter(username => username && username.toLowerCase().includes(query.toLowerCase()))
          .filter(username => username.toLowerCase() !== existingUsernameLower) // Exclude the leaving user
          .filter((username, index, array) => array.indexOf(username) === index)
          .slice(0, 10);

        setSubstituteSuggestions(suggestions);
        setShowSubstituteSuggestions(suggestions.length > 0);
      } else {
        setSubstituteSuggestions([]);
        setShowSubstituteSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching substitute suggestions:', error);
      setSubstituteSuggestions([]);
      setShowSubstituteSuggestions(false);
    } finally {
      setIsLoadingSubstituteSuggestions(false);
    }
  };

  const fetchManagerSuggestions = async (query) => {
    if (!selectedTenant || query.length < 2) {
      setManagerSuggestions([]);
      setShowManagerSuggestions(false);
      return;
    }

    try {
      setIsLoadingManagerSuggestions(true);
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/assets?tenant=${selectedTenant.name}&search=${encodeURIComponent(query)}&field=username`);
      const data = await response.json();

      if (response.ok && data.assets) {
        const suggestions = data.assets
          .map(asset => asset.username)
          .filter(username => username && username.toLowerCase().includes(query.toLowerCase()))
          .filter((username, index, array) => array.indexOf(username) === index)
          .slice(0, 10);

        setManagerSuggestions(suggestions);
        setShowManagerSuggestions(suggestions.length > 0);
      } else {
        setManagerSuggestions([]);
        setShowManagerSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching manager suggestions:', error);
      setManagerSuggestions([]);
      setShowManagerSuggestions(false);
    } finally {
      setIsLoadingManagerSuggestions(false);
    }
  };

  const fetchUserAssets = async (assigneeLabel) => {
    setIsLoadingAssets(true);
    try {
      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/users/assets?tenant=${encodeURIComponent(selectedTenant.name)}&assignee=${encodeURIComponent(assigneeLabel)}`
      );
      const data = await response.json();

      if (response.ok && data.success !== false) {
        const uniqueAssets = [];
        const seenSerials = new Set();

        (data.assets || []).forEach((asset) => {
          const serial = asset.serial;
          if (serial && !seenSerials.has(serial)) {
            seenSerials.add(serial);
            uniqueAssets.push(asset);
          } else if (!serial) {
            uniqueAssets.push(asset);
          }
        });

        const defaultActions = {};
        uniqueAssets.forEach((asset) => {
          const key = getAssetKey(asset);
          if (key) defaultActions[key] = 'wipe';
        });

        setUserAssets(uniqueAssets);
        setUserLicenses(data.licenses || []);
        setAssetActions(defaultActions);
      } else {
        setUserAssets([]);
        setUserLicenses([]);
        setAssetActions({});
      }
    } catch (error) {
      console.error('Error fetching user assets:', error);
      setUserAssets([]);
      setUserLicenses([]);
      setAssetActions({});
    } finally {
      setIsLoadingAssets(false);
    }
  };

  const handleAssetActionChange = (assetKey, action) => {
    setAssetActions((prev) => ({
      ...prev,
      [assetKey]: action
    }));
  };

  const handleAssigneeSelect = async (assigneeLabel) => {
    setFormData((prev) => ({
      ...prev,
      existingUsername: assigneeLabel
    }));
    setShowSuggestions(false);
    setUsernameSuggestions([]);

    if (formData.action === 'offboard') {
      try {
        const response = await authenticatedFetch(
          `${getApiBaseUrl()}/api/users/details?tenant=${encodeURIComponent(selectedTenant.name)}&username=${encodeURIComponent(assigneeLabel)}`
        );
        const data = await response.json();

        if (response.ok && data.found) {
          setFormData((prev) => ({
            ...prev,
            existingUsername: assigneeLabel,
            name: data.name,
            surname: data.surname
          }));
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
      }

      await fetchUserAssets(assigneeLabel);
    }
  };

  const handleSubstituteSelect = (username) => {
    setFormData(prev => ({
      ...prev,
      substituteFor: username
    }));
    setShowSubstituteSuggestions(false);
    setSubstituteSuggestions([]);
  };

  const handleManagerSelect = (username) => {
    setFormData((prev) => ({
      ...prev,
      manager: username
    }));
    setTemplateValues((prev) => {
      const next = { ...prev };
      templateFields.forEach((field, index) => {
        if (isManagerField(field)) {
          next[templateFieldKey(field, index)] = username;
        }
      });
      return next;
    });
    setShowManagerSuggestions(false);
    setManagerSuggestions([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');
    
    if (!selectedTenant) {
      setSubmitMessage('Error: No tenant selected');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await authenticatedFetch('/api/create-onboarding-ticket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tenant: selectedTenant.name,
          formData: {
            ...formData,
            name:
              formData.name ||
              findTemplateValueByNames(templateFields, templateValues, ['Name']) ||
              '',
            surname:
              formData.surname ||
              findTemplateValueByNames(templateFields, templateValues, ['Surname']) ||
              '',
            phoneNumber:
              formData.phoneNumber ||
              findTemplateValueByNames(templateFields, templateValues, ['Phone Number']) ||
              '',
            manager:
              formData.manager ||
              findTemplateValueByNames(templateFields, templateValues, ['Manager']) ||
              '',
            employeeStartDate:
              formData.employeeStartDate ||
              findTemplateValueByNames(templateFields, templateValues, ['Employee Start Date']) ||
              '',
            timeLimitedContract: timeLimitedValue,
            templateFields: formData.action === 'onboard' ? buildTemplateFieldsPayload() : [],
            assetCollectionActions: userAssets.map((asset) => ({
              id: asset.id,
              serial: asset.serial,
              manufacturer: asset.manufacturer,
              model: asset.model,
              action: assetActions[getAssetKey(asset)] || 'wipe'
            }))
          }
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitMessage('✅ Successful - Your request has been processed');
        setTicketUrl(data.ticketUrl);
        
        // Reset form after successful submission
        setTimeout(() => {
          setFormData({
            name: '',
            surname: '',
            phoneNumber: '',
            manager: '',
            action: 'onboard',
            employerLeavingDate: '',
            employeeStartDate: '',
            timeLimitedContract: 'no',
            contractEndDate: '',
            existingUsername: '',
            additionalInformation: '',
            substituteFor: ''
          });
          setSubmitMessage('');
          setTicketUrl('');
          setUsernameSuggestions([]);
          setShowSuggestions(false);
          setSubstituteSuggestions([]);
          setShowSubstituteSuggestions(false);
          setManagerSuggestions([]);
          setShowManagerSuggestions(false);
          setUserAssets([]);
          setUserLicenses([]);
          setAssetActions({});
          fetchOnboardingTemplate();
        }, 5000);
      } else {
        const detailText =
          typeof data.details === 'string'
            ? data.details
            : data.details?.error || data.details?.description || '';
        const message = data.error || 'Failed to process your request';
        setSubmitMessage(`❌ ${message}${detailText ? `: ${detailText}` : ''}`);
        setTicketUrl('');
      }
      
    } catch (error) {
      console.error('Error creating ticket:', error);
      setSubmitMessage('❌ Failed to process your request (network error)');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="onoffboard-form-container">
      <div className="form-header">
        <button onClick={onBack} className="back-button">
          <Icon name="arrowUp" size="small" />
          Back to Self Service
        </button>
        <h2>User On/Offboarding</h2>
        <p>Request user account activation or deactivation</p>
      </div>

      <div className="form-content">
        <div className="action-selector">
          <div className="action-buttons">
            <button 
              type="button"
              className={`action-btn ${formData.action === 'onboard' ? 'active' : ''}`}
              onClick={() => setFormData(prev => ({ ...prev, action: 'onboard' }))}
            >
              <Icon name="plus" size="small" />
              Onboard User
            </button>
            <button 
              type="button"
              className={`action-btn ${formData.action === 'offboard' ? 'active' : ''}`}
              onClick={() => setFormData(prev => ({ ...prev, action: 'offboard' }))}
            >
              <Icon name="minus" size="small" />
              Offboard User
            </button>
          </div>
        </div>

        {formData.action === 'offboard' && (
          <div className="form-section">
            <h3>Offboarding Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="existingUsername">Decommission user *</label>
                <div className="typeahead-container">
                  <input
                    type="text"
                    id="existingUsername"
                    name="existingUsername"
                    value={formData.existingUsername}
                    onChange={handleInputChange}
                    onFocus={() => {
                      if (usernameSuggestions.length > 0) {
                        setShowSuggestions(true);
                      }
                    }}
                    onBlur={() => {
                      // Delay hiding suggestions to allow clicking on them
                      setTimeout(() => setShowSuggestions(false), 200);
                    }}
                    required
                    placeholder="Search assigned to (assets) or display name…"
                    autoComplete="off"
                  />
                  {isLoadingSuggestions && (
                    <div className="typeahead-loading">
                      <div className="loading-spinner"></div>
                    </div>
                  )}
                  {showSuggestions && usernameSuggestions.length > 0 && (
                    <div className="typeahead-suggestions">
                      {usernameSuggestions.map((username, index) => (
                        <div
                          key={index}
                          className="suggestion-item"
                          onClick={() => handleAssigneeSelect(username)}
                        >
                          {username}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <small className="form-help">
                  Search by assigned to (assets) or display name (directory). Matching resolves all related UPNs and
                  usernames to list licenses and assets.
                </small>
              </div>
              <div className="form-group">
                <label htmlFor="employerLeavingDate">Employee Last Day *</label>
                <input
                  type="date"
                  id="employerLeavingDate"
                  name="employerLeavingDate"
                  value={formData.employerLeavingDate}
                  onChange={handleInputChange}
                  required
                />
                <small className="form-help">When is the employee's last working day?</small>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="substituteFor">Substitute</label>
                <div className="typeahead-container">
                  <input
                    type="text"
                    id="substituteFor"
                    name="substituteFor"
                    value={formData.substituteFor}
                    onChange={handleInputChange}
                    onFocus={() => {
                      if (substituteSuggestions.length > 0) {
                        setShowSubstituteSuggestions(true);
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => setShowSubstituteSuggestions(false), 200);
                    }}
                    placeholder="Start typing to search for substitute..."
                    autoComplete="off"
                  />
                  {isLoadingSubstituteSuggestions && (
                    <div className="typeahead-loading">
                      <div className="loading-spinner"></div>
                    </div>
                  )}
                  {showSubstituteSuggestions && substituteSuggestions.length > 0 && (
                    <div className="typeahead-suggestions">
                      {substituteSuggestions.map((username, index) => (
                        <div
                          key={index}
                          className="suggestion-item"
                          onClick={() => handleSubstituteSelect(username)}
                        >
                          {username}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <small className="form-help">Optional: Enter the username of the employee who will take over</small>
              </div>
              <div className="form-group">
                {/* Empty for layout balance */}
              </div>
            </div>
            
            {/* Show user assets and licenses if username is selected */}
            {formData.existingUsername && (
              <div className="form-row">
                <div className="form-group full-width">
                  <label>Assets ({userAssets.length}) &amp; licenses ({userLicenses.length})</label>
                  {isLoadingAssets ? (
                    <div className="assets-loading">
                      <div className="loading-spinner"></div>
                      <span>Loading assets and licenses...</span>
                    </div>
                  ) : (
                    <div className="user-resources">
                      {/* Assets Section */}
                      {userAssets.length > 0 ? (
                        <div className="resource-section">
                          <h4>Assets to be collected</h4>
                          <div className="user-assets">
                            {userAssets.map((asset, index) => {
                              const assetKey = getAssetKey(asset) || `asset-${index}`;
                              return (
                              <div key={assetKey} className="asset-item">
                                <div className="asset-info">
                                  <strong>{asset.manufacturer || 'Unknown'} {asset.model || 'Unknown'}</strong>
                                  <div className="asset-details">
                                    <span>Serial: {asset.serial || 'N/A'}</span>
                                    {asset.LastConnected && (
                                      <span>Last connected: {new Date(asset.LastConnected).toLocaleDateString()}</span>
                                    )}
                                  </div>
                                </div>
                                <div className="asset-action-select">
                                  <label htmlFor={`asset-action-${assetKey}`}>Next step</label>
                                  <select
                                    id={`asset-action-${assetKey}`}
                                    value={assetActions[assetKey] || 'wipe'}
                                    onChange={(e) => handleAssetActionChange(assetKey, e.target.value)}
                                  >
                                    {ASSET_ACTION_OPTIONS.map((opt) => (
                                      <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="resource-section">
                          <h4>Assets to be collected</h4>
                          <div className="no-resources">
                            <span>No assets found for this user</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Licenses Section */}
                      {userLicenses.length > 0 ? (
                        <div className="resource-section">
                          <h4>Licenses to be Released:</h4>
                          <div className="user-licenses">
                            {userLicenses
                              .filter((license) => getLicenseDisplayName(license))
                              .map((license, index) => (
                              <div key={index} className="license-item">
                                <div className="license-info">
                                  <strong>{getLicenseDisplayName(license)}</strong>
                                  <div className="license-details">
                                    {license.billingCycle && (
                                      <span>Billing: {license.billingCycle}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="resource-section">
                          <h4>Licenses to be Released:</h4>
                          <div className="no-resources">
                            <span>No licenses found for this user</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <small className="form-help">
                    Choose the next step for each asset. Licenses listed below will be released for all resolved UPNs.
                  </small>
                </div>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="onoffboard-form">
          {formData.action === 'onboard' ? (
            templateLoading ? (
              <div className="form-section">
                <div className="form-help">Loading onboarding form…</div>
              </div>
            ) : (
              <>
                {templateSections.map((section) => (
                  <div key={`section-${section.maincaption}`} className="form-section">
                    <h3>{section.maincaption}</h3>
                    {section.sectiondescription ? (
                      <p className="form-help section-description">{section.sectiondescription}</p>
                    ) : null}

                    <div className="form-row template-fields-grid">
                      {(section.otherFields || []).map((field) => {
                        const globalIndex = templateFields.findIndex(
                          (entry) => templateFieldKey(entry) === templateFieldKey(field)
                        );
                        const index = globalIndex >= 0 ? globalIndex : 0;
                        const key = templateFieldKey(field, index);
                        const value = templateValues[key] ?? defaultValueForField(field);
                        const required = Boolean(field.mandatory);
                        const label = `${field.displayname}${required ? ' *' : ''}`;
                        const inputId = `template-field-${key}`;

                        if (field.fieldtype === 'textbox') {
                          return (
                            <div key={key} className="form-group full-width">
                              <label htmlFor={inputId}>{label}</label>
                              <textarea
                                id={inputId}
                                value={value}
                                onChange={(e) => setTemplateFieldValue(field, index, e.target.value)}
                                placeholder={field.description || ''}
                                rows={4}
                                required={required}
                              />
                              {field.description ? (
                                <small className="form-help">{field.description}</small>
                              ) : null}
                            </div>
                          );
                        }

                        if (field.fieldtype === 'date') {
                          return (
                            <div key={key} className="form-group">
                              <label htmlFor={inputId}>{label}</label>
                              <input
                                type="date"
                                id={inputId}
                                value={value}
                                onChange={(e) => setTemplateFieldValue(field, index, e.target.value)}
                                required={required}
                              />
                              {field.description ? (
                                <small className="form-help">{field.description}</small>
                              ) : null}
                            </div>
                          );
                        }

                        if (field.fieldtype === 'yesno') {
                          return (
                            <div key={key} className="form-group">
                              <label htmlFor={inputId}>{label}</label>
                              <select
                                id={inputId}
                                value={value || 'no'}
                                onChange={(e) => setTemplateFieldValue(field, index, e.target.value)}
                                required={required}
                              >
                                <option value="no">No</option>
                                <option value="yes">Yes</option>
                              </select>
                              {field.description ? (
                                <small className="form-help">{field.description}</small>
                              ) : null}
                            </div>
                          );
                        }

                        // text (default)
                        if (isManagerField(field)) {
                          return (
                            <div key={key} className="form-group">
                              <label htmlFor={inputId}>{label}</label>
                              <div className="typeahead-container">
                                <input
                                  type="text"
                                  id={inputId}
                                  value={value}
                                  onChange={(e) => {
                                    const next = e.target.value;
                                    setTemplateFieldValue(field, index, next);
                                    if (next.length >= 2) fetchManagerSuggestions(next);
                                    else {
                                      setManagerSuggestions([]);
                                      setShowManagerSuggestions(false);
                                    }
                                  }}
                                  onFocus={() => {
                                    if (managerSuggestions.length > 0) setShowManagerSuggestions(true);
                                  }}
                                  onBlur={() => {
                                    setTimeout(() => setShowManagerSuggestions(false), 200);
                                  }}
                                  required={required}
                                  placeholder={field.description || 'Start typing to search for manager...'}
                                  autoComplete="off"
                                />
                                {isLoadingManagerSuggestions && (
                                  <div className="typeahead-loading">
                                    <div className="loading-spinner"></div>
                                  </div>
                                )}
                                {showManagerSuggestions && managerSuggestions.length > 0 && (
                                  <div className="typeahead-suggestions">
                                    {managerSuggestions.map((username, suggestionIndex) => (
                                      <div
                                        key={`${username}-${suggestionIndex}`}
                                        className="suggestion-item"
                                        onClick={() => handleManagerSelect(username)}
                                      >
                                        {username}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              {field.description ? (
                                <small className="form-help">{field.description}</small>
                              ) : null}
                            </div>
                          );
                        }

                        return (
                          <div key={key} className="form-group">
                            <label htmlFor={inputId}>{label}</label>
                            <input
                              type="text"
                              id={inputId}
                              value={value}
                              onChange={(e) => setTemplateFieldValue(field, index, e.target.value)}
                              placeholder={field.description || ''}
                              required={required}
                            />
                            {field.description ? (
                              <small className="form-help">{field.description}</small>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>

                    {timeLimitedValue === 'yes' &&
                      section.maincaption === 'Personal Information' && (
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="contractEndDate">Contract End Date</label>
                          <input
                            type="date"
                            id="contractEndDate"
                            name="contractEndDate"
                            value={formData.contractEndDate}
                            min={
                              findTemplateValueByNames(templateFields, templateValues, [
                                'Employee Start Date'
                              ]) || undefined
                            }
                            onChange={handleInputChange}
                          />
                          <small className="form-help">If set, the account should expire on this date.</small>
                        </div>
                      </div>
                    )}

                    {(section.checkboxGroups || []).length > 0 && (
                      <div className="onboarding-access-widgets">
                        {section.checkboxGroups.map((group) => (
                          <div key={`group-${section.maincaption}-${group.name}`} className="onboarding-access-widget">
                            <h4>{group.name}</h4>
                            <div className="onboarding-access-options">
                              {(group.options || []).map((option) => {
                                const globalIndex = templateFields.findIndex(
                                  (entry) => templateFieldKey(entry) === templateFieldKey(option)
                                );
                                const index = globalIndex >= 0 ? globalIndex : 0;
                                const key = templateFieldKey(option, index);
                                return (
                                  <label key={key} className="checkbox-label">
                                    <input
                                      type="checkbox"
                                      checked={Boolean(templateValues[key])}
                                      onChange={(e) =>
                                        setTemplateFieldValue(option, index, e.target.checked)
                                      }
                                    />
                                    <span className="checkbox-custom" />
                                    <span>{option.displayname}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {timeLimitedValue === 'yes' &&
                  !templateSections.some((section) => section.maincaption === 'Personal Information') && (
                  <div className="form-section">
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="contractEndDate">Contract End Date</label>
                        <input
                          type="date"
                          id="contractEndDate"
                          name="contractEndDate"
                          value={formData.contractEndDate}
                          onChange={handleInputChange}
                        />
                        <small className="form-help">If set, the account should expire on this date.</small>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )
          ) : (
            <div className="form-section">
              <h3>Additional Information</h3>
              <div className="form-group full-width">
                <label htmlFor="additionalInformation">
                  Additional Information about Leaving Employee
                </label>
                <textarea
                  id="additionalInformation"
                  name="additionalInformation"
                  value={formData.additionalInformation}
                  onChange={handleInputChange}
                  placeholder="Enter any additional details, notes, or special instructions..."
                  rows={4}
                />
                <small className="form-help">Optional: Add any relevant notes or special instructions</small>
              </div>
            </div>
          )}

          {submitMessage && (
            <div className={`submit-message ${submitMessage.includes('Error') ? 'error' : 'success'}`}>
              <Icon name={submitMessage.includes('Error') ? 'exclamation' : 'checkmark'} size="small" />
              <div>
                <div>{submitMessage}</div>
                {ticketUrl && (
                  <div className="ticket-link">
                    <a href={ticketUrl} target="_blank" rel="noopener noreferrer" className="ticket-url">
                      <Icon name="external" size="small" />
                      View Ticket in Zendesk
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="form-actions">
            <button type="button" onClick={onBack} className="cancel-button">
              Cancel
            </button>
            <button 
              type="submit" 
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="loading-spinner"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Icon name="checkmark" size="small" />
                  {formData.action === 'onboard' ? 'Onboard User' : 'Offboard User'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OnOffboardForm;
